
import { v4 as uuidv4 } from "uuid";

import { SpellLevel } from "@dndModels/Types";

/**
 * This is a generic term for Spell Slots, Ki Points, Sorcery Points,
 * Lay on Hands, Healing Light, etc.  
 * It also could work for Material Spell Components, but I don't 
 * want to worry about that right now.
 * level is for spell slots (and pact slots), 
 * and it's what level the slot is.
 * total is the total number of that resource they have.
 * used is how many of them are currently used.
 * refreshRule is when this refreshes (generally short or long).
 */
export class CharacterSlot {
  type_id = ""; // ID of the table it connects to
  level: SpellLevel = 1;
  true_total = 0;
  true_used = 0;
  true_created = 0;
  slot_name = "";

  _resource_true_id = "";
  get resource_true_id() {
    return this._resource_true_id !== "" ? this._resource_true_id : `${this.slot_name}_${this.level}_${this.type_id}`;
  }
  set resource_true_id(value: string) {
    this._resource_true_id = value;
  }

  constructor(obj?: any) {
    this.type_id = obj ? obj.type_id : "";
    this.level = obj ? obj.level : 1;
    this.true_total = obj ? +obj.total : 0;
    this.true_used = obj ? +obj.used : 0;
    this.true_created = obj && obj.created ? +obj.created : 0;
    this.slot_name = obj && obj.slot_name ? obj.slot_name : "";
  }

  toDBObj(): any {
    return {
      type_id: this.type_id,
      level: this.level,
      total: this.true_total,
      used: this.true_used,
      created: this.true_created
    };
  }

  get total(): number {
    return this.true_total + this.created;
  }

  set total(value: number) {
    this.true_total = value;
  }

  get created(): number {
    return this.true_created;
  }

  set created(value: number) {
    if (this.true_used > 0) {
      this.true_used -= value;
      if (this.true_used < 0) {
        this.true_created = -1 * this.true_used;
        this.true_used = 0;
      }
    } else {
      this.true_created = value;
    }
  }

  get used(): number {
    return this.true_used;
  }

  set used(value: number) {
    if (this.true_created > 0) {
      this.true_created -= value;
      if (this.true_created < 0) {
        if (this.true_total > 0) {
          this.true_used = -1 * this.true_created;
          if (this.true_used < 0) {
            this.true_used = 0;
          }
        }
        this.true_created = 0;
      }
    } else {
      this.true_used = value;
    }
  }

  get is_resource(): boolean {
    return this._resource_true_id !== "";
  }

  clone(): CharacterSlot {
    const clone = new CharacterSlot();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: CharacterSlot): void {
    this.resource_true_id = copyMe.resource_true_id;
    this.type_id = copyMe.type_id;
    this.level = copyMe.level;
    this.true_total = copyMe.true_total;
    this.true_used = copyMe.true_used;
    this.true_created = copyMe.true_created;
    this.slot_name = copyMe.slot_name;
  }
}

export class CharacterInfusionsOfLevel {
  true_id = "";
  type_id = ""; // ID of the table it connects to
  source_true_id = "";
  level: SpellLevel = 1;
  total = 0;
  infusions: CharacterInfusion[] = [];

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.type_id = obj.type_id ? obj.type_id : "";
      this.source_true_id = obj.source_true_id ? obj.source_true_id : "";
      this.level = obj.level ? obj.level : 1;
      this.total = obj.total ? obj.total : 0;
      this.infusions = obj.infusions ? obj.infusions.map((o: any) => new CharacterInfusion(o)) : [];
    }
  }

  get infused() {
    return this.infusions.filter(o => o.char_spell_id !== "").length;
  }

  get used() {
    return this.infusions.filter(o => o.char_spell_id !== "" && o.used).length;
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      type_id: this.type_id,
      source_true_id: this.source_true_id,
      level: this.level,
      total: this.total,
      infusions: this.infusions.map(o => o.toDBObj())
    };
  }

  clone(): CharacterInfusionsOfLevel {
    const clone = new CharacterInfusionsOfLevel();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: CharacterInfusionsOfLevel): void {
    this.true_id = copyMe.true_id;
    this.type_id = copyMe.type_id;
    this.source_true_id = copyMe.source_true_id;
    this.level = copyMe.level;
    this.total = copyMe.total;
    this.infusions = copyMe.infusions.map(o => o.clone());
  }
}

export class CharacterInfusion {
  true_id = "";
  level: SpellLevel = 1;
  char_spell_id = "";
  char_item_id = "";
  ally_name = "";
  ally_item = "";
  used = false;
  source_true_id = "";
  // infusion_mik_id: string = "";

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.level = obj.level ? obj.level : 1;
      this.char_spell_id = obj.char_spell_id ? obj.char_spell_id : "";
      this.char_item_id = obj.char_item_id ? obj.char_item_id : "";
      this.ally_name = obj.ally_name ? obj.ally_name : "";
      this.ally_item = obj.ally_item ? obj.ally_item : "";
      this.used = obj.used ? obj.used : false;
      this.source_true_id = obj.source_true_id ? obj.source_true_id : "";
      // this.infusion_mik_id = obj.infusion_mik_id ? obj.infusion_mik_id : "";
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      level: this.level,
      char_spell_id: this.char_spell_id,
      char_item_id: this.char_item_id,
      ally_name: this.ally_name,
      ally_item: this.ally_item,
      used: this.used,
      source_true_id: this.source_true_id,
      // infusion_mik_id: this.infusion_mik_id
    };
  }

  clone(): CharacterInfusion {
    const clone = new CharacterInfusion();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: CharacterInfusion): void {
    this.true_id = copyMe.true_id;
    this.level = copyMe.level;
    this.char_spell_id = copyMe.char_spell_id;
    this.char_item_id = copyMe.char_item_id;
    this.ally_name = copyMe.ally_name;
    this.ally_item = copyMe.ally_item;
    this.used = copyMe.used;
    this.source_true_id = copyMe.source_true_id;
    // this.infusion_mik_id = copyMe.infusion_mik_id;
  }
}

export class CharacterInfusionForAlly {
  true_id = "";
  level: SpellLevel = 1;
  spell_id = "";
  char_item_id = "";
  ally_name = "";
  used = false;
  // Make a spot where these can be set for whole character, 
  // and they'll automatically populate when infusions are set.
  dc = 8; 
  spell_attack = 0;
  mod = 0;
  on_hit = false;
  on_struck = false;
  or_boon = false;

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.level = obj.level ? obj.level : 1;
      this.spell_id = obj.spell_id ? obj.spell_id : "";
      this.char_item_id = obj.char_item_id ? obj.char_item_id : "";
      this.ally_name = obj.ally_name ? obj.ally_name : "";
      this.used = obj.used ? obj.used : false;
      this.dc = obj.dc ? obj.dc : 8;
      this.spell_attack = obj.spell_attack ? obj.spell_attack : 0;
      this.mod = obj.mod ? obj.mod : 0;
      this.on_hit = obj.on_hit ? obj.on_hit : false;
      this.on_struck = obj.on_struck ? obj.on_struck : false;
      this.or_boon = obj.or_boon ? obj.or_boon : false;
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      level: this.level,
      spell_id: this.spell_id,
      char_item_id: this.char_item_id,
      ally_name: this.ally_name,
      used: this.used,
      dc: this.dc,
      spell_attack: this.spell_attack,
      mod: this.mod,
      on_hit: this.on_hit,
      on_struck: this.on_struck,
      or_boon: this.or_boon,
    };
  }

  clone(): CharacterInfusionForAlly {
    const clone = new CharacterInfusionForAlly();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: CharacterInfusionForAlly): void {
    this.true_id = copyMe.true_id;
    this.level = copyMe.level;
    this.spell_id = copyMe.spell_id;
    this.char_item_id = copyMe.char_item_id;
    this.ally_name = copyMe.ally_name;
    this.used = copyMe.used;
    this.dc = copyMe.dc;
    this.spell_attack = copyMe.spell_attack;
    this.mod = copyMe.mod;
    this.on_hit = copyMe.on_hit;
    this.on_struck = copyMe.on_struck;
    this.or_boon = copyMe.or_boon;
  }
}

export class InfusionAllyDetails {
  true_id = "";
  ally_name = "";
  dc = 8; 
  spell_attack = 0;
  mod = 0;
  on_hit = false;
  on_struck = false;
  or_boon = false;

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.ally_name = obj.ally_name ? obj.ally_name : "";
      this.dc = obj.dc ? obj.dc : 8;
      this.spell_attack = obj.spell_attack ? obj.spell_attack : 0;
      this.mod = obj.mod ? obj.mod : 0;
      this.on_hit = obj.on_hit ? obj.on_hit : false;
      this.on_struck = obj.on_struck ? obj.on_struck : false;
      this.or_boon = obj.or_boon ? obj.or_boon : false;
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      ally_name: this.ally_name,
      dc: this.dc,
      spell_attack: this.spell_attack,
      mod: this.mod,
      on_hit: this.on_hit,
      on_struck: this.on_struck,
      or_boon: this.or_boon,
    };
  }

  clone(): InfusionAllyDetails {
    const clone = new InfusionAllyDetails();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: InfusionAllyDetails): void {
    this.true_id = copyMe.true_id;
    this.ally_name = copyMe.ally_name;
    this.dc = copyMe.dc;
    this.spell_attack = copyMe.spell_attack;
    this.mod = copyMe.mod;
    this.on_hit = copyMe.on_hit;
    this.on_struck = copyMe.on_struck;
    this.or_boon = copyMe.or_boon;
  }
}